import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { ServiceDepartmentModule } from '@navio/nsdk';
import { limitSummerTires, limitWinterTires, recommendedSummerTires, recommendedWinterTires } from 'noddi-util';
import { type FleetViewFilterInput, FleetViewSummaryCharts, FleetViewSummaryChartsTranslations } from 'noddi-web';
import { useAvailableBrandModules } from '../../../hooks/useAvailableBrandModules';

type Props = {
  /** The fleet list's `filters.apiInput`. */
  filterParams: FleetViewFilterInput;
};

export const FleetViewSummaryCards = ({ filterParams }: Props) => {
  const { availableModules, isLoading, error } = useAvailableBrandModules();

  if (error) {
    return <ApiErrorMessageWithTrans error={error} />;
  }
  // Wheel storage and tire wear only mean something where the brand stores wheels or sells tires.
  const hasTireModule =
    availableModules.has(ServiceDepartmentModule.TIRE_SALES) ||
    availableModules.has(ServiceDepartmentModule.TIRE_HOTEL);
  if (isLoading || !hasTireModule) {
    return null;
  }

  return <FleetViewSummaryCharts filterParams={filterParams} translations={getFleetTranslations()} />;
};

const getFleetTranslations = (): FleetViewSummaryChartsTranslations => {
  return {
    storedWheelSetsBySeason: t`Wheel set in storage`,
    notOnTireHotel: t`None`,
    summerTires: t`Summer tires`,
    winterTires: t`Winter tires`,
    noCarsInFleet: t`No cars in fleet`,
    carsHaveWheelsStored: (stored: number, total: number) =>
      t`${stored} of ${total} cars have wheel sets stored with us`,
    summerTireCondition: t`Summer tire condition`,
    winterTireCondition: t`Winter tire condition`,
    shouldBeReplaced: t`Should be replaced`,
    mustBeReplaced: t`Must be replaced`,
    goodCondition: t`Good condition`,
    noMeasurements: t`No measurements`,
    noMeasurementsAvailable: t`No measurements available`,
    carsWithMeasurements: (measured: number, total: number) => t`${measured} of ${total} cars have tire measurements`,
    upcomingBookings: t`Upcoming bookings`,
    hasUpcomingBooking: t`Has booking`,
    noUpcomingBooking: t`No booking`,
    carsWithUpcomingBookings: (withBooking: number, total: number) =>
      t`${withBooking} of ${total} cars have upcoming bookings`,
    tireWearInfo: {
      summerTitle: t`Summer tire wear information`,
      winterTitle: t`Winter tire wear information`,
      goodCondition: t`Good condition`,
      shouldBeReplaced: t`Should be replaced`,
      mustBeReplaced: t`Must be replaced`,
      regulationsTitle: t`Regulations and our recommendations`,
      summerRegulationsDescription: t`In Norway, the minimum requirement for tread depth on summer tires is ${limitSummerTires} mm. It is illegal to drive with summer tires that are under ${limitSummerTires} mm. We recommend changing tires when the tread depth is below ${recommendedSummerTires} mm. When the tread depth is below ${recommendedSummerTires} mm, the braking distance increases considerably, and the tire loses its performance.`,
      winterRegulationsDescription: t`In Norway, the minimum requirement for tread depth on winter tires is ${limitWinterTires} mm. It is illegal to drive with winter tires that are under ${limitWinterTires} mm. We recommend changing tires when the tread depth is below ${recommendedWinterTires} mm. When the tread depth is below ${recommendedWinterTires} mm, the braking distance increases considerably, and the tire loses its performance.`
    }
  };
};
